import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { StatusName } from "../factories/statuses/enums/status.enum";
import {
  StatusModel,
  SubStatusModel,
} from "../factories/statuses/models/status.model";

export interface DashboardStatusModel {
  statusName: string;
  statusCount: number;
  subStatuses: {
    subStatusName: string;
    subStatusCount: number;
  }[];
}

type GetDashboardStatusesParams = {
  orgId: string;
  userId?: string | null;
  workTypeId?: string | null;
};

export type AddWorkTypeSubStatusPayload = {
  subStatus: string;
  workTypeStatusId: string;
  isChecked?: boolean;
  serviceIds?: Array<string | null>;
};

const dashboardStatusOrder = [
  "Ready",
  "In Progress",
  "Waiting",
  "Completed",
] as const;

const toNumber = (value: unknown) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeName = (value: string) =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

const getDashboardStatusOrder = (statusName?: string | null) => {
  if (!statusName) {
    return Number.MAX_SAFE_INTEGER;
  }

  const index = (dashboardStatusOrder as readonly string[]).indexOf(statusName);

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const normalizeDashboardStatusesResponse = (
  res: unknown,
): DashboardStatusModel[] => {
  if (!Array.isArray(res)) {
    return [];
  }

  return res
    .map((rawItem) => {
      const item = rawItem as any;

      return {
        statusName: String(item?.statusName ?? ""),
        statusCount: toNumber(item?.statusCount),
        subStatuses: Object.entries(item?.subStatuses || {}).map(
          ([key, value]) => ({
            subStatusName: key,
            subStatusCount: toNumber(value),
          }),
        ),
      };
    })
    .filter(
      (item) =>
        item.statusName &&
        normalizeName(item.statusName) !== normalizeName(StatusName.trial),
    )
    .sort(
      (a, b) =>
        getDashboardStatusOrder(a.statusName) -
        getDashboardStatusOrder(b.statusName),
    );
};

/** How many work items use a status and where they can go (QA batch 16, SET-014). */
export type SubStatusUsage = {
  id: string;
  name: string;
  groupName: string;
  workCount: number;
  /** id null = the main status itself (no sub-status). */
  moveTargets: { id: string | null; name: string }[];
};

export const statusesSlice = createApi({
  reducerPath: "statusesSlice",
  tagTypes: ["Status"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();

      headers.set("Authorization", authHeader.Authorization);

      return headers;
    },
  }),
  endpoints: (build) => ({
    getStatuses: build.query<
      StatusModel[],
      {
        orgId: string;
      }
    >({
      query: (params) => ({
        url: "api/work-type-status/get-all",
        params,
        method: "GET",
      }),
      transformResponse: (res: StatusModel[]) => {
        return (
          res
            ?.filter((status) => status?.name !== StatusName.trial)
            .sort((a, b) => a.order - b.order) || []
        );
      },
      providesTags: ["Status"],
    }),

    getDashboardStatuses: build.query<
      DashboardStatusModel[],
      GetDashboardStatusesParams
    >({
      query: ({ orgId, userId, workTypeId }) => ({
        url: `api/dashboard/get-status-by-assignee-workType/${orgId}`,
        params: {
          userId: userId ?? "null",
          workTypeId: workTypeId ?? "null",
        },
        method: "GET",
      }),
      transformResponse: normalizeDashboardStatusesResponse,
      providesTags: ["Status"],
    }),

    updateSubStatus: build.mutation<unknown, SubStatusModel>({
      query: (body) => ({
        url: "api/work-type-status/update-work-type-sub-status",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Status"],
    }),

    addSubStatus: build.mutation<SubStatusModel, AddWorkTypeSubStatusPayload>({
      query: (body) => ({
        url: "api/work-type-status/add-work-type-sub-status",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Status"],
    }),

    deleteSubStatus: build.mutation<
      unknown,
      SubStatusModel & {
        /** Where its work items go: "group" or another status id (QA batch 16). */
        moveTo?: string;
      }
    >({
      query: ({ moveTo, ...body }) => ({
        url: "api/work-type-status/delete-work-type-sub-status",
        body,
        params: moveTo ? { moveTo } : undefined,
        method: "DELETE",
      }),
      invalidatesTags: ["Status"],
    }),
    getSubStatusUsage: build.query<SubStatusUsage, string>({
      query: (id) => ({
        url: `api/work-type-status/sub-status-usage/${id}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),

    updateStatusWork: build.mutation<
      unknown,
      { workId: string; statusId: string; subStatusId: string }
    >({
      query: (params) => ({
        url: "api/work/update-status",
        params,
        method: "PUT",
      }),
      invalidatesTags: ["Status"],
    }),
  }),
});

export const {
  useGetStatusesQuery,
  useLazyGetStatusesQuery,
  useGetDashboardStatusesQuery,
  useLazyGetDashboardStatusesQuery,
  useUpdateSubStatusMutation,
  useAddSubStatusMutation,
  useDeleteSubStatusMutation,
  useLazyGetSubStatusUsageQuery,
  useUpdateStatusWorkMutation,
} = statusesSlice;
